// Standardized edit/create modal shell. Wraps `FixedSizeDialog` (fixed-height
// shell so content changes don't make the modal jump) and adds a consistent
// header (title + dirty pill + extras) and footer (Cancel / Save / optional
// Save & Close + extras slot for domain-specific workflow buttons).
//
// Contract:
//   - Caller owns the body fields (`children`).
//   - Caller's `onSubmit({ close })` is called on either Save button; `close`
//     is `true` when "Save and close" was clicked, `false` for "Save". The
//     caller decides whether to call `onOpenChange(false)` after a successful
//     save (typically: `if (close) onOpenChange(false)`).
//   - `headerExtras` renders to the right of the title (status badge, language
//     toggle, history button, print, etc.).
//   - `footerExtras` renders on the LEFT of the footer (domain-specific
//     workflow buttons like "Mark Ready for Invoice", "View Order", etc.).
//     Standard Cancel/Save/Save & Close stay anchored to the right.
//   - `confirmDiscardOnClose` + `isDirty` intercepts the close attempt and
//     prompts an AlertDialog before discarding.
//
// Use this for every long-form create/edit modal in the app so they have one
// shared look-and-feel. For simple confirmation dialogs, use AlertDialog
// directly — this primitive is overkill there.

import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FixedSizeDialog, type FixedDialogSize } from './FixedSizeDialog';

export interface EntityFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Tailwind max-width tier. Defaults to `2xl`. */
  size?: FixedDialogSize;

  // ---- Header ----
  /** Title node — caller can render any combination of icon + text + inline
   *  editors + version label (no string-only constraint). */
  title: ReactNode;
  /** Optional small descriptive line under the title. */
  description?: ReactNode;
  /** Right-side header toolbar slot (status badge, language, print, etc.). */
  headerExtras?: ReactNode;
  /** When true, shows an "Unsaved changes" pill next to the title. */
  isDirty?: boolean;
  /** Override the dirty-pill text. */
  dirtyLabel?: string;

  // ---- Body ----
  children: ReactNode;

  // ---- Footer ----
  /** Disables Save buttons + shows a spinner while a save is in flight. */
  isSubmitting?: boolean;
  /** Disable the Save buttons (e.g. when the form is invalid). Caller still
   *  sees the button — it's just not clickable. Spinner state stays driven by
   *  `isSubmitting` so an in-flight save and an invalid form look different. */
  saveDisabled?: boolean;
  cancelLabel?: string;       // default "Cancel"
  saveLabel?: string;         // default "Save"
  saveAndCloseLabel?: string; // default "Save and close"
  /** Renders a second primary "Save and close" button. */
  showSaveAndClose?: boolean;
  /** Custom cancel handler. Defaults to `onOpenChange(false)` (with discard
   *  confirmation if `confirmDiscardOnClose` is set). */
  onCancel?: () => void;
  /** Called when either Save button is clicked. `close=true` when the user
   *  hit "Save and close". Caller decides when to actually close. */
  onSubmit: (opts: { close: boolean }) => void | Promise<void>;
  /** Left-side footer slot for domain-specific workflow buttons. */
  footerExtras?: ReactNode;
  /** Hide the Cancel button (rare — useful when caller fully owns the footer
   *  via `footerExtras`). */
  hideCancel?: boolean;
  /** Hide the Save button (rare — read-only view dialogs). */
  hideSave?: boolean;

  // ---- Discard confirmation ----
  /** When true + isDirty, close attempts prompt a confirmation dialog. */
  confirmDiscardOnClose?: boolean;
  discardTitle?: string;
  discardDescription?: string;
  discardConfirmLabel?: string;
  discardCancelLabel?: string;
}

export function EntityFormDialog({
  open,
  onOpenChange,
  size = '2xl',
  title,
  description,
  headerExtras,
  isDirty = false,
  dirtyLabel,
  children,
  isSubmitting = false,
  saveDisabled = false,
  cancelLabel = 'Cancel',
  saveLabel = 'Save',
  saveAndCloseLabel = 'Save and close',
  showSaveAndClose = false,
  onCancel,
  onSubmit,
  footerExtras,
  hideCancel = false,
  hideSave = false,
  confirmDiscardOnClose = false,
  discardTitle = 'Discard changes?',
  discardDescription = 'Unsaved changes will be lost.',
  discardConfirmLabel = 'Discard',
  discardCancelLabel = 'Keep editing',
}: EntityFormDialogProps) {
  // Remembered between the button click and the form submit event, so the
  // submit handler can tell the caller whether to close after success.
  const closeAfterSaveRef = useRef(false);
  const [showDiscard, setShowDiscard] = useState(false);

  const requestClose = () => {
    if (confirmDiscardOnClose && isDirty) {
      setShowDiscard(true);
      return;
    }
    if (onCancel) onCancel();
    else onOpenChange(false);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const close = closeAfterSaveRef.current;
    closeAfterSaveRef.current = false;
    await onSubmit({ close });
  };

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(next) => {
          if (next) onOpenChange(true);
          else requestClose();
        }}
      >
        <FixedSizeDialog size={size} scroll="section-scroll">
          <DialogHeader>
            <div className="flex items-start justify-between gap-3 pr-8">
              <div className="min-w-0 flex-1">
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {title}
                  {isDirty && (
                    <Badge variant="secondary" className="text-xs">
                      {dirtyLabel || 'Unsaved changes'}
                    </Badge>
                  )}
                </DialogTitle>
                {description && (
                  <DialogDescription className="mt-1">
                    {description}
                  </DialogDescription>
                )}
              </div>
              {headerExtras && (
                <div className="flex items-center gap-1 shrink-0">
                  {headerExtras}
                </div>
              )}
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
            <div className="flex-1 overflow-y-auto pr-2">{children}</div>
            <DialogFooter className="gap-2 sm:gap-2 mt-4 border-t pt-3">
              {footerExtras && (
                <div className="flex items-center gap-2 mr-auto flex-wrap">
                  {footerExtras}
                </div>
              )}
              {!hideCancel && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={requestClose}
                  disabled={isSubmitting}
                >
                  {cancelLabel}
                </Button>
              )}
              {!hideSave && (
                <>
                  <Button
                    type="submit"
                    variant={showSaveAndClose ? 'outline' : 'default'}
                    disabled={isSubmitting || saveDisabled}
                    onClick={() => { closeAfterSaveRef.current = false; }}
                  >
                    {isSubmitting && !closeAfterSaveRef.current && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    {saveLabel}
                  </Button>
                  {showSaveAndClose && (
                    <Button
                      type="submit"
                      disabled={isSubmitting || saveDisabled}
                      onClick={() => { closeAfterSaveRef.current = true; }}
                    >
                      {isSubmitting && closeAfterSaveRef.current && (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      )}
                      {saveAndCloseLabel}
                    </Button>
                  )}
                </>
              )}
            </DialogFooter>
          </form>
        </FixedSizeDialog>
      </Dialog>

      {confirmDiscardOnClose && (
        <AlertDialog open={showDiscard} onOpenChange={setShowDiscard}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{discardTitle}</AlertDialogTitle>
              <AlertDialogDescription>{discardDescription}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{discardCancelLabel}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => {
                  setShowDiscard(false);
                  onOpenChange(false);
                }}
              >
                {discardConfirmLabel}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
}
